import { useState } from "react";
import { isAddress, type Address } from "viem";
import type { EstadoVault } from "../hooks/useVault";
import type { useAcciones } from "../hooks/useAcciones";
import { useAvisos, TIPOS_AVISO } from "../hooks/useAvisos";
import type { RedKeel } from "../config/networks";
import { corto, num } from "../lib/formato";
import { useT } from "../lib/i18n";
import { useConfirmar } from "./Confirmar";
import { Giro } from "./Modal";

type Acc = ReturnType<typeof useAcciones>;
type Pedir = ReturnType<typeof useConfirmar>["pedir"];

/**
 * QUÉ MEJORA CADA VERSIÓN DE LÓGICA. Mapa direccion-de-la-logica → novedades, para que la tarjeta
 * "Actualizar la lógica" diga en cristiano qué gana el usuario, no un "hay una versión nueva" a secas.
 * Al publicar una lógica nueva en una factory, se registra aquí su dirección (en minúscula) con sus
 * mejoras. Sin entrada → la tarjeta cae al texto genérico.
 *
 * Dos listas por honestidad: `siempre` son mejoras del ALGORITMO de reajuste, que se adoptan en
 * cualquier vault al actualizar; `nuevoCascaron` son features que viven en el cascarón del vault
 * (colchón, objetivo, referidos, recenter) y SOLO las tienen los vaults de generación nueva —se
 * ocultan si `colchonSoportado` es false, para no prometer algo que ese vault no puede usar.
 */
type Novedades = { siempre: string[]; nuevoCascaron?: string[] };
const V2: Novedades = {
  siempre: [
    "Recupera mejor tras una caída: la posición de recuperación nace ya dentro del rango y vende lo mínimo, en vez de malvender de golpe.",
    "Decide la salida con el precio real del momento, no con un promedio: deja de cristalizar pérdidas cuando el mercado está fino.",
    "Tu ganancia ya apartada no se toca en ningún reajuste.",
  ],
  nuevoCascaron: [
    "Vuelve solo al rango estrecho sin vender tu activo cuando el precio regresa.",
    "Colchón de recuperación configurable: eliges cuánto margen dejar sobre tu capital.",
    "Reparto de referidos activo en las comisiones.",
  ],
};
// 2026-09-29: protección de capital por los DOS lados (beta).
const PROTEGE_CAPITAL: Novedades = {
  siempre: [
    "Si tu piscina vale menos que tu capital y el precio sale por ARRIBA, ya no se queda con la pérdida en dólares: compra el activo y abre un rango cuyo techo te devuelve tu capital.",
    "Con pérdida, el robot ya no puede reajustar con el precio dentro del rango: se acabó perder el rango de recuperación por un rebote de un tick.",
    "Nunca vende con pérdida. A cambio, no garantiza cuándo sales: si el precio no llega al techo, espera.",
  ],
};
// 2026-10-29: achicar rango con reinyección promediada (incluye todo lo de PROTEGE_CAPITAL).
const ACHICAR_PROMEDIADO: Novedades = {
  siempre: [
    "«Achicar rango» con reinyección promediada: tu aporte COMPRA el activo y el rango se recalcula para que, al tocar el techo, salgas con tu capital MÁS lo que aportaste.",
    "El techo de recuperación ahora sí baja cuando aportas: cuanto más aportas, menos tiene que subir el precio para que recuperes.",
    "Nunca vende el activo ni gasta más de lo que aportas. Si la pool no tiene profundidad para la compra, la operación se cancela entera y no se mueve nada.",
    ...PROTEGE_CAPITAL.siempre,
  ],
};
// 2026-10-29 (2ª): redistribuir sin aporte (incluye todo lo de ACHICAR_PROMEDIADO).
const REDISTRIBUIR: Novedades = {
  siempre: [
    "«Redistribuir sin aporte»: si el precio subió dentro de tu rango, el estable que tu piscina ya tiene compra el activo y el rango se estrecha, saliendo con tu capital sin poner dinero.",
    "Te muestra a qué precio se paga sola cada amplitud y cuánto perderías de más si el activo cae después.",
    ...ACHICAR_PROMEDIADO.siempre,
  ],
};
// 2026-10-30: redistribución PROGRAMADA (incluye todo lo de REDISTRIBUIR).
const ORDEN_PROGRAMADA: Novedades = {
  siempre: [
    "Redistribución programada: eliges un ancho en «Redistribuir sin aporte», tu piscina queda en espera y el robot la redistribuye solo cuando el precio llega al gatillo. La cancelas cuando quieras.",
    "Al ejecutarse, el contrato vuelve a comprobar que sales con tu capital; si no, no hace nada y sigue esperando.",
    ...REDISTRIBUIR.siempre,
  ],
};
// Las 4 lógicas v2 (una por red) comparten las mismas novedades.
const NOVEDADES_LOGICA: Record<string, Novedades> = {
  "0x019e2ae4c4c3e830e8b8d312bd25d7b51e682f05": ORDEN_PROGRAMADA, // Arbitrum
  "0x2edffabf2d886c97b42d89c602f4a5487f961eeb": ORDEN_PROGRAMADA, // Robinhood
  "0x97813916c80853e1eb9232b80ab267b2baace4a7": ORDEN_PROGRAMADA, // HyperEVM
  "0xa7b5f562de7042902d299b312c56b2e143d1b498": ORDEN_PROGRAMADA, // BNB
  "0x0ef59be4a0f873d657e843f782f591d375cdeed3": ORDEN_PROGRAMADA, // Base
  "0x3b733a2a77097c70deb1efdc61e76bed98340573": ORDEN_PROGRAMADA, // Ethereum
  "0x57ce635aee097c5aacd228d87674320032c211b6": REDISTRIBUIR, // Arbitrum
  "0x698f02d098e850a7f96e590d9b243e2a1c313a9f": REDISTRIBUIR, // Robinhood
  "0xa1b1458ac60650647c57c89d76fcfbd343b32a60": REDISTRIBUIR, // HyperEVM
  "0x4cd557b5e7b7e9088b67f0e77e3e795afe4fb353": REDISTRIBUIR, // BNB
  "0xa6b3f22aa4b55762af7163500dda0ef9731bd8eb": REDISTRIBUIR, // Base
  "0x42ac79f159236f86a639a659e65c29c6a6b78795": ACHICAR_PROMEDIADO, // Arbitrum
  "0x5a7c8dcec69193a4d30976b5103024be930c4957": ACHICAR_PROMEDIADO, // Robinhood
  "0x0bc8d0e200ed974c7287affd215f0e7862c36d26": ACHICAR_PROMEDIADO, // HyperEVM
  "0x38dab562ec4658c2ad56cd088440a233ff43b1b1": ACHICAR_PROMEDIADO, // BNB
  "0xc34f67fd7bff5fb0b88885dde052b25c30411f07": ACHICAR_PROMEDIADO, // Base
  "0xadb3734fe6bda27802869daec9dc2bcbff19898e": ACHICAR_PROMEDIADO, // Ethereum
  "0xe4dea15d814be09611fcc4492b909dda85c18e29": PROTEGE_CAPITAL, // Robinhood beta
  // 2026-09-30: la lógica de la beta llega a PRODUCCIÓN (vaults nuevos; Base también publica upgrade).
  "0x7373aa1a03d81eaf74cf565c5087fd0f2b5e4f8a": PROTEGE_CAPITAL, // Base
  "0x07c49ad9a83cd7e0d7b8964718e7988b5ad0dc17": PROTEGE_CAPITAL, // Arbitrum
  "0x707d17a8548ac53c8fce4090982b2dedfa98780d": PROTEGE_CAPITAL, // Robinhood
  "0xcae173205a582a4ef58baef1aa388933019b773f": PROTEGE_CAPITAL, // HyperEVM
  "0x9335ba057fca911ccb31d9d81175f13a4d8bafc6": PROTEGE_CAPITAL, // BNB
  "0x52dc92c7e3fdbd4fff7892dfc9dc7bc1d7a01ecf": PROTEGE_CAPITAL, // Ethereum
  "0xf8bb8c2901e78eb1d949e8a44edf4d42001f8925": V2, // Arbitrum
  "0x67f845f851f6cadf43bc7d73907649b29a99c29b": V2, // Robinhood
  "0x6222a399335567e6a1ee0dc5fd92d188da4e39ec": V2, // HyperEVM
  "0x0c4e62f1197fe12dc72bb15cac650347b8b059dc": V2, // BNB
};
function novedadesDe(dir: Address, cascaronNuevo: boolean): { puntos: string[]; ocultas: boolean } {
  const n = NOVEDADES_LOGICA[dir.toLowerCase()];
  if (!n) return { puntos: [], ocultas: false };
  const extra = n.nuevoCascaron ?? [];
  return { puntos: cascaronNuevo ? [...n.siempre, ...extra] : n.siempre, ocultas: !cascaronNuevo && extra.length > 0 };
}

/**
 * REDISEÑO 2026-08-30: Mejor jerarquía visual, ayuda contextual, campos más claros.
 *
 * Secciones temáticas agrupadas + cada ajuste es una tarjeta con título, explicación clara,
 * controles y contexto. Se añade clase `aj-con-ayuda` para campos que pueden necesitar más info.
 */
export function Ajustes({ v, acciones, red }: { v: EstadoVault; acciones: Acc; red: RedKeel }) {
  const t = useT();
  const ocupado = !!acciones.haciendo || acciones.soloLectura;
  const { pedir, modal } = useConfirmar();

  return (
    <div className="ajustes-secciones">
      {/* AGRUPADO POR TEMA, no en una rejilla plana de diez tarjetas iguales (2026-08-22). Cuatro
          secciones con encabezado: primero lo que define cómo gana, luego cómo se protege en las
          caídas, luego las guardias técnicas (que casi no se tocan), y al final quién la gestiona.
          Dentro de cada sección, las tarjetas van de lo que se toca más a lo que se toca menos.
          MEJORA: encabezados más visuales, subtextos contextuales. */}
      <Seccion
        titulo={t("Estrategia")}
        sub={t("Cómo y cuándo gana dinero la piscina. Aquí va lo que tocas más.")}
      >
        <RangoAncho v={v} acciones={acciones} pedir={pedir} />
        <Reinversion v={v} acciones={acciones} pedir={pedir} />
      </Seccion>

      <Seccion
        titulo={t("Ante caídas")}
        sub={t("Qué hace el robot cuando el precio se mueve en tu contra. Protección automática.")}
      >
        <Recuperacion v={v} acciones={acciones} pedir={pedir} />
        {v.colchonSoportado && <Colchon v={v} acciones={acciones} pedir={pedir} />}
        <Reinyeccion v={v} acciones={acciones} pedir={pedir} />
      </Seccion>

      <Seccion
        titulo={t("Guardias del reajuste")}
        sub={t("Protecciones técnicas contra manipulación de precios. Casi nunca necesitan cambios.")}
      >
        <Guardias v={v} acciones={acciones} pedir={pedir} />
      </Seccion>

      <Seccion
        titulo={t("El robot")}
        sub={t("Quién gestiona la posición y cómo enterarte de lo que hace.")}
      >
        <Cuota acciones={acciones} ocupado={ocupado} cuota={v.cuota} pedir={pedir} />
        <Robot v={v} acciones={acciones} pedir={pedir} />
        {v.hayActualizacion && <Actualizar v={v} acciones={acciones} pedir={pedir} />}
        {v.esMio && <AvisosTelegram v={v} red={red} />}
      </Seccion>
      {modal}
    </div>
  );
}

/** Una sección temática de ajustes: encabezado + rejilla con sus tarjetas. */
function Seccion({ titulo, sub, children }: { titulo: string; sub: string; children: React.ReactNode }) {
  return (
    <section className="aj-seccion">
      <div className="aj-seccion-cab">
        <h3>{titulo}</h3>
        <p>{sub}</p>
      </div>
      <div className="aj-rejilla">{children}</div>
    </section>
  );
}

/**
 * Un ajuste. Deja de ser un `<details>` plegado: en rejilla caben todos a la vista, y un
 * desplegable cerrado no dice que hay dentro — obligaba a abrir seis para encontrar el que se
 * buscaba.
 *
 * MEJORA: mejor separación de contenido (titulo + descripcion + controles + botón).
 */
function Bloque({
  titulo,
  children
}: {
  titulo: string
  children: React.ReactNode
}) {
  return (
    <div className="panel">
      <div className="cuerpo-panel aj">
        <div className="titulo-aj"><b>{titulo}</b></div>
        {children}
      </div>
    </div>
  );
}

/**
 * Ayuda contextual expandible. Muestra un icono ? que expande el texto de ayuda.
 */
function AyudaContextual({ texto }: { texto: React.ReactNode }) {
  const t = useT();
  const [abierta, setAbierta] = useState(false);
  return (
    <div className="aj-ayuda">
      <button
        className="aj-ayuda-btn"
        onClick={() => setAbierta(!abierta)}
        aria-expanded={abierta}
        title={t("Más información")}
      >
        ?
      </button>
      {abierta && (
        <div className="aj-ayuda-contenido">
          {texto}
        </div>
      )}
    </div>
  );
}

function RangoAncho({ v, acciones, pedir }: { v: EstadoVault; acciones: Acc; pedir: Pedir }) {
  const t = useT();
  const ocupado = !!acciones.haciendo || acciones.soloLectura;
  // Arranca en el modo que YA tiene el vault: si los dos semianchos difieren, la banda es asimétrica.
  const yaAsimetrico = v.asimSoportado && v.ticksBelow > 0 && v.ticksAbove > 0 && v.ticksBelow !== v.ticksAbove;
  const [modo, setModo] = useState<"sim" | "asim">(yaAsimetrico ? "asim" : "sim");
  const [half, setHalf] = useState(String(v.halfWidthTicks));
  // Los campos por lado nacen de la forma actual (o del semiancho simétrico si aún no hay asimetría).
  const [below, setBelow] = useState(String(v.ticksBelow || v.halfWidthTicks));
  const [above, setAbove] = useState(String(v.ticksAbove || v.halfWidthTicks));

  const limites = (
    <>{t("Límites por lado: mínimo")} <code>{v.spacing}</code> {t("(exigido por la pool), máximo")} <code>20.000</code> {t("ticks.")}</>
  );

  return (
    <Bloque titulo={t("Ancho del rango")}>
      <p className="detalle">
        {t("El")} <strong>{t("semiancho")}</strong> {t("(±% del precio) determina cuánta comisión cobras y cuánto gas gasta el robot.")}
        {v.asimSoportado && " " + t("Puedes dejar más hueco a un lado que al otro; la forma se conserva cuando el robot re-centra.")}
      </p>

      {v.asimSoportado && (
        <div className="conmutador">
          <button className={`ancho ${modo === "sim" ? "activo" : ""}`} disabled={ocupado}
            onClick={() => setModo("sim")}>{t("Simétrico")}</button>
          <button className={`ancho ${modo === "asim" ? "activo" : ""}`} disabled={ocupado}
            onClick={() => setModo("asim")}>{t("Asimétrico")}</button>
        </div>
      )}

      {modo === "sim" ? (
        <>
          <div className="aj-campo-con-ayuda">
            <Campo etiqueta={t("Semiancho del rango (ticks)")} val={half} set={setHalf} disabled={ocupado} />
            <AyudaContextual texto={
              <>
                <strong>{t("Más estrecho (números bajos):")}</strong> {t("cobras más comisión cada vez, pero el robot reajusta más a menudo (gasta más gas).")}
                <br /><strong>{t("Más ancho (números altos):")}</strong> {t("menos comisiones, menos reajustes, menos gas.")}
                <br />{limites}
              </>
            } />
          </div>
          <button className="btn" disabled={ocupado}
            onClick={() => pedir({
              titulo: t("Cambiar el ancho del rango"),
              descripcion: <>{t("El ancho nuevo se usa en el PROXIMO reajuste. La posición de ahora no se toca.")}</>,
              detalles: [[t("Semiancho ahora"), t("{n} ticks", { n: v.halfWidthTicks })], [t("Semiancho nuevo"), t("{n} ticks", { n: half })]],
              boton: t("Guardar rango"),
              ejecutar: () => acciones.guardarRango(Number(half)),
            })}>{t("Guardar rango")}</button>
        </>
      ) : (
        <>
          <div className="dos">
            <Campo etiqueta={t("Semiancho abajo (ticks)")} val={below} set={setBelow} disabled={ocupado} />
            <Campo etiqueta={t("Semiancho arriba (ticks)")} val={above} set={setAbove} disabled={ocupado} />
          </div>
          <p className="detalle">{limites}</p>
          <button className="btn" disabled={ocupado}
            onClick={() => pedir({
              titulo: t("Cambiar el ancho del rango"),
              descripcion: <>{t("La banda asimétrica se usa en el PROXIMO reajuste y se conserva mientras siga la posición. La de ahora no se toca.")}</>,
              detalles: [
                [t("Abajo ahora"), t("{n} ticks", { n: v.ticksBelow || v.halfWidthTicks })],
                [t("Abajo nuevo"), t("{n} ticks", { n: Number(below) })],
                [t("Arriba nuevo"), t("{n} ticks", { n: Number(above) })],
              ],
              boton: t("Guardar rango"),
              ejecutar: () => acciones.guardarRangoAsym(Number(below), Number(above)),
            })}>{t("Guardar rango")}</button>
        </>
      )}
    </Bloque>
  );
}

function Guardias({ v, acciones, pedir }: { v: EstadoVault; acciones: Acc; pedir: Pedir }) {
  const t = useT();
  const [twap, setTwap] = useState(String(v.twapWindow));
  const [desv, setDesv] = useState(String(v.maxTwapDeviationTicks));
  const [slip, setSlip] = useState(String(v.swapSlippageBps));
  const [cd, setCd] = useState(String(v.cooldown));
  const ocupado = !!acciones.haciendo || acciones.soloLectura;

  return (
    <Bloque titulo={t("Guardias del reajuste")}>
      <p className="detalle">
        {t("Protegen contra manipulación: si el precio spot se aleja demasiado del precio medio (TWAP), la transacción revierte.")}
      </p>
      <div className="dos-campos">
        <Campo etiqueta={t("Ventana del TWAP (s)")} val={twap} set={setTwap} disabled={ocupado} />
        <Campo etiqueta={t("Desviación máxima (ticks)")} val={desv} set={setDesv} disabled={ocupado} />
        <Campo etiqueta={t("Deslizamiento máx. (bps)")} val={slip} set={setSlip} disabled={ocupado} />
        <Campo etiqueta={t("Entre reajustes (s)")} val={cd} set={setCd} disabled={ocupado} />
      </div>
      <button className="btn" disabled={ocupado}
        onClick={() => pedir({
          titulo: t("Cambiar las guardias"),
          descripcion: <>{t("Son lo que hace seguro que el robot dispare mirando el precio spot: si spot y TWAP se separan mas de la desviacion, la transaccion revierte.")}</>,
          detalles: [
            [t("Ventana del TWAP"), t("{a} s  →  {b} s", { a: v.twapWindow, b: twap })],
            [t("Desviacion maxima"), t("{a}  →  {b} ticks", { a: v.maxTwapDeviationTicks, b: desv })],
            [t("Deslizamiento"), t("{a}  →  {b} bps", { a: v.swapSlippageBps, b: slip })],
            [t("Tiempo entre reajustes"), t("{a} s  →  {b} s", { a: v.cooldown, b: cd })],
          ],
          aviso: Number(desv) > 400 ? <>{t("Una desviación alta deja pasar precios más manipulables.")}</> : undefined,
          boton: t("Guardar guardias"),
          ejecutar: () => acciones.guardarGuardias(Number(twap), Number(desv), Number(slip), Number(cd)),
        })}>
        {t("Guardar guardias")}
      </button>
    </Bloque>
  );
}

function Reinyeccion({ v, acciones, pedir }: { v: EstadoVault; acciones: Acc; pedir: Pedir }) {
  const t = useT();
  const [modo, setModo] = useState(String(v.reinjectMode));
  const [pct, setPct] = useState(String(v.reinjectPctBps / 100));
  const [caida, setCaida] = useState(String(v.dropStepBps / 100));
  const ocupado = !!acciones.haciendo || acciones.soloLectura;

  return (
    <Bloque titulo={t("Comprar si el precio baja")}>
      <p className="detalle">
        {t("Suelta dinero poco a poco cuando el precio cae, para comprar más barato (acumulación automática).")}
      </p>
      <label className="campo">
        <span>{t("Cuándo reinyectar")}</span>
        <select value={modo} onChange={(e) => setModo(e.target.value)} disabled={ocupado}>
          <option value="0">{t("Nunca — dinero dormido")}</option>
          <option value="1">{t("En cada reajuste del rango")}</option>
          <option value="2">{t("Cada vez que el precio cae otro tanto (%)")}</option>
        </select>
      </label>
      <Campo etiqueta={t("Cuánto suelta (% de la reserva original)")} val={pct} set={setPct} disabled={ocupado} />
      {modo === "2" && (
        <Campo etiqueta={t("Por cada caída de (%)")} val={caida} set={setCaida} disabled={ocupado} />
      )}
      {v.dropStepsDone > 0 && (
        <p className="detalle">{t("Tramos ya usados:")} <strong>{v.dropStepsDone}</strong>. {t("Evita reinyectar de más.")}</p>
      )}
      <button className="btn" disabled={ocupado}
        onClick={() => pedir({
          titulo: t("Cambiar la reinyección"),
          descripcion: <>{t("Decide cuándo se suelta la reserva que tienes apartada.")}</>,
          detalles: [
            [t("Cuándo"), [t("Nunca"), t("En cada reajuste"), t("Por cada caída")][Number(modo)] ?? "?"],
            [t("Cuánto suelta"), t("{pct}% de la reserva original", { pct })],
            ...(Number(modo) === 2 ? [[t("Por cada caída de"), t("{caida}%", { caida })] as [string, string]] : []),
          ],
          aviso: Number(modo) === 0 ? <>{t("Con «nunca» la reserva no hace nada — está parada.")}</> : undefined,
          boton: t("Guardar reinyección"),
          ejecutar: () => acciones.guardarReinyeccion(
            Number(modo), Math.round(Number(pct) * 100), Math.round(Number(caida) * 100)
          ),
        })}>
        {t("Guardar reinyección")}
      </button>
    </Bloque>
  );
}

function Recuperacion({ v, acciones, pedir }: { v: EstadoVault; acciones: Acc; pedir: Pedir }) {
  const t = useT();
  const [on, setOn] = useState(v.ilRecover);
  const [maxTicks, setMaxTicks] = useState(String(v.ilRecoverMaxTicks));
  const [ventaMin, setVentaMin] = useState(v.minSaleOn);
  const [maxBps, setMaxBps] = useState(String(v.minSaleMaxBps / 100));
  const ocupado = !!acciones.haciendo || acciones.soloLectura;

  return (
    <Bloque titulo={t("Recuperar tras una caída")}>
      <p className="detalle">
        {t("Cuando el precio se sale por abajo, el rango se estira automáticamente para recuperar tu capital. Sin esto, el reajuste normal re-centra y cristaliza la pérdida.")}
      </p>
      <label className="ck">
        <input type="checkbox" checked={on} onChange={(e) => setOn(e.target.checked)} disabled={ocupado} />
        <span>{t("Estirar el rango para recuperar el capital")}</span>
      </label>
      <label className="ck">
        <input type="checkbox" checked={ventaMin} onChange={(e) => setVentaMin(e.target.checked)} disabled={ocupado} />
        <span>{t("Vender lo mínimo para que la posición nazca DENTRO del rango")}</span>
      </label>
      <Campo etiqueta={t("Estiramiento máximo (ticks)")} val={maxTicks} set={setMaxTicks} disabled={ocupado} />
      <Campo etiqueta={t("Venta máxima (% del saldo)")} val={maxBps} set={setMaxBps} disabled={ocupado} />
      <button className="btn" disabled={ocupado}
        onClick={() => pedir({
          titulo: t("Cambiar la recuperación"),
          descripcion: <>{t("Es lo que evita cristalizar la pérdida cuando el precio se sale por abajo: en vez de re-centrar, el rango se estira hacia donde tiene que volver.")}</>,
          detalles: [
            [t("Recuperación"), on ? t("activada") : t("APAGADA")],
            [t("Estiramiento máximo"), t("{n} ticks", { n: maxTicks })],
            [t("Vender el mínimo"), ventaMin ? t("sí") : t("no")],
            [t("Venta máxima"), t("{n}%", { n: maxBps })],
          ],
          aviso: !on ? <>{t("Apagada: un reajuste con el precio por debajo")} <strong>{t("re-centra y pierde dinero")}</strong>.</> : undefined,
          peligro: !on,
          boton: t("Guardar recuperación"),
          ejecutar: () => acciones.guardarRecuperacion(on, Number(maxTicks), ventaMin, Math.round(Number(maxBps) * 100)),
        })}>
        {t("Guardar recuperación")}
      </button>
    </Bloque>
  );
}

function Colchon({ v, acciones, pedir }: { v: EstadoVault; acciones: Acc; pedir: Pedir }) {
  const t = useT();
  // EL COLCHÓN ES UN IMPORTE FIJO, NO UN %. Lo que cuesta salir de verdad —comisión de la pool,
  // deslizamiento y el 0,10% de conversión— es casi el mismo puñado de dólares con 600 $ dentro que
  // con 2.000 $; con el % el extra crecía con el capital y estiraba el rango de más. Los vaults
  // abiertos antes de este cambio siguen con su porcentaje (`recoverMarginRef` a cero) y para ellos
  // se sigue mostrando en %, que es lo que su contrato usará.
  const fijo = v.colchonFijoSoportado && v.recoverMarginRef > 0n;
  const enUnidades = Number(v.recoverMarginRef) / 10 ** v.ref.decimals;
  const [importe, setImporte] = useState(fijo ? String(enUnidades) : "3");
  const [pct, setPct] = useState(String(v.recoverMarginBps / 100));
  const ocupado = !!acciones.haciendo || acciones.soloLectura;

  const usarFijo = v.colchonFijoSoportado;
  const n = Number(usarFijo ? importe : pct);
  const valido = Number.isFinite(n) && n >= 0 && (usarFijo ? true : n <= 5);
  // OJO (2026-10-02): el contrato desplegado NO recorta el colchón al usarlo (solo el setter lo
  // comprueba con el vault abierto). Se enseña el 5% como tope recomendado y la app ya no lo deja pasar.
  // El contrato recorta el colchón al 5% del capital: el mismo tope duro que tenía el porcentaje,
  // porque el colchón se paga en ancho de rango y el ancho es liquidez diluida. Se enseña aquí para
  // que nadie ponga 50 $ en una piscina de 200 $ y luego no entienda el rango que sale.
  const tope = v.objetivo / 20;
  const aplicado = usarFijo ? Math.min(valido ? n : 0, tope) : v.objetivo * (valido ? n : 0) / 100;
  const meta = v.objetivo + aplicado;
  const recortado = usarFijo && valido && n > tope;

  return (
    <Bloque titulo={t("Salir con algo de sobra")}>
      <p className="detalle">
        {t("El rango de recuperación apunta a devolverte exactamente lo que pusiste. Un")} <strong>{t("colchón")}</strong> {t("añade un margen extra que cubre el coste real de salir (comisión, slippage, 0,10% conversión).")}
      </p>
      {usarFijo ? (
        <Campo etiqueta={t("Colchón fijo ({sym})", { sym: v.ref.symbol })} val={importe} set={setImporte} disabled={ocupado} />
      ) : (
        <Campo etiqueta={t("Colchón sobre el capital (%)")} val={pct} set={setPct} disabled={ocupado} />
      )}
      <div className="aj-dinero-mostracion">
        <div className="aj-dinero-linea">
          <span>{t("Capital:")}</span>
          <strong>{num(v.objetivo, 2)} {v.ref.symbol}</strong>
        </div>
        <div className="aj-dinero-linea">
          <span>{t("Colchón:")}</span>
          <strong>+{num(aplicado, 2)} {v.ref.symbol}</strong>
        </div>
        <div className="aj-dinero-linea aj-dinero-meta">
          <span>{t("El rango apuntará a:")}</span>
          <strong>{num(meta, 2)} {v.ref.symbol}</strong>
        </div>
      </div>
      {recortado && (
        <p className="detalle">
          {t("Recortado al tope: el colchón nunca pasa del 5% del capital ({max} {sym}) para no ensanchar el rango de más.", { max: num(tope, 2), sym: v.ref.symbol })}
        </p>
      )}
      <p className="detalle tenue">
        {usarFijo
          ? t("Piscinas nuevas nacen con 3 {sym}. Salir cuesta casi lo mismo tengas 600 o 2.000 dentro, así que el colchón es un importe fijo y no un porcentaje que crece contigo.", { sym: v.ref.symbol })
          : t("Esta piscina se abrió antes del colchón fijo y sigue con el porcentual. Máximo: 5% (más ancho = menos comisiones).")}
      </p>
      <button className="btn" disabled={ocupado || !valido}
        onClick={() => pedir({
          titulo: t("Cambiar el colchón"),
          descripcion: <>{t("Hasta dónde apunta el rango cuando está recuperando una caída.")}</>,
          detalles: [
            [t("Colchón"), n === 0
              ? t("ninguno — al capital pelado")
              : usarFijo ? `+${num(aplicado, 2)} ${v.ref.symbol}` : t("{pct}% por encima", { pct })],
            [t("Capital"), `${num(v.objetivo, 2)} ${v.ref.symbol}`],
            [t("El rango apuntará a"), `${num(meta, 2)} ${v.ref.symbol}`],
          ],
          aviso: n === 0
            ? <>{t("Sin colchón el rango apunta al capital exacto.")} <strong>{t("Salir de verdad cuesta más")}</strong>.</>
            : <>{t("Colchón mayor = rango más ancho =")} <strong>{t("menos comisiones")}</strong>.</>,
          boton: t("Guardar colchón"),
          ejecutar: () => usarFijo
            ? acciones.guardarColchonFijo(BigInt(Math.round(n * 10 ** v.ref.decimals)))
            : acciones.guardarColchon(Math.round(n * 100)),
        })}>
        {t("Guardar colchón")}
      </button>
    </Bloque>
  );
}

function Reinversion({ v, acciones, pedir }: { v: EstadoVault; acciones: Acc; pedir: Pedir }) {
  const t = useT();
  const ocupado = !!acciones.haciendo || acciones.soloLectura;
  return (
    <Bloque titulo={t("Qué hacer con lo ganado")}>
      <p className="detalle">
        {t("Ahora mismo las ganancias")} <strong>{v.reinvertir ? t("se reinvierten") : t("se apartan")}</strong>.
      </p>
      <div className="aj-estado-toggle">
        <div className="aj-estado-actual">
          {v.reinvertir ? (
            <>
              <strong>{t("Reinvierten")}</strong>
              <p>{t("Vuelven a trabajar en la posición → más comisiones.")}</p>
            </>
          ) : (
            <>
              <strong>{t("Se apartan")}</strong>
              <p>{t("Tú controlas cuándo retirarte ese dinero.")}</p>
            </>
          )}
        </div>
      </div>
      <button className="btn" disabled={ocupado}
        onClick={() => pedir({
          titulo: v.reinvertir ? t("Apartar las ganancias") : t("Reinvertir las ganancias"),
          descripcion: v.reinvertir
            ? <>{t("Las ganancias volverán a apartarse. Ni el reajuste ni el barrido pueden tocarlas: solo tú decides.")}</>
            : <>{t("Las ganancias dejarán de apartarse y volverán a la posición a generar más comisiones.")}</>,
          detalles: [[t("Ahora"), v.reinvertir ? t("se reinvierten") : t("se apartan")],
                     [t("Después"), v.reinvertir ? t("se apartan") : t("se reinvierten")]],
          boton: v.reinvertir ? t("Apartar") : t("Reinvertir"),
          ejecutar: () => acciones.cambiarReinversion(!v.reinvertir),
        })}>
        {v.reinvertir ? t("Apartar las ganancias") : t("Reinvertir las ganancias")}
      </button>
    </Bloque>
  );
}

function Cuota({ acciones, ocupado, cuota, pedir }: { acciones: Acc; ocupado: boolean; cuota: number; pedir: Pedir }) {
  const t = useT();
  const [n, setN] = useState("50");
  return (
    <Bloque titulo={t("Permisos del robot")}>
      <p className="detalle">
        {t("Quedan")} <strong className={cuota === 0 ? "error" : cuota < 10 ? "aviso" : ""}>{cuota}</strong> {t("reajustes.")}
      </p>
      <div className="aj-barra-cuota">
        <div className="aj-cuota-visual" style={{ opacity: Math.min(cuota / 100, 1) }} />
      </div>
      {cuota === 0 && <p className="detalle error">{t("Sin cuota, el robot no reajusta. Recarga ahora.")}</p>}
      {cuota < 10 && cuota > 0 && <p className="detalle aviso">{t("Pocas quedan. Recargue pronto.")}</p>}
      <Campo etiqueta={t("Añadir reajustes")} val={n} set={setN} disabled={ocupado} />
      <button className="btn" disabled={ocupado} onClick={() => pedir({
        titulo: t("Recargar la cuota"),
        descripcion: <>{t("La cuota es cuántos reajustes permites. Es un contador aparte de la bolsa de gas: el gas paga, la cuota autoriza.")}</>,
        detalles: [[t("Ahora"), t("{n} reajustes", { n: cuota })], [t("Después"), t("{n} reajustes", { n: cuota + Number(n) })]],
        boton: t("Recargar cuota"),
        ejecutar: () => acciones.recargarCuota(Number(n)),
      })}>
        {t("Recargar cuota")}
      </button>
    </Bloque>
  );
}

function Robot({ v, acciones, pedir }: { v: EstadoVault; acciones: Acc; pedir: Pedir }) {
  const t = useT();
  const [dir, setDir] = useState("");
  const ocupado = !!acciones.haciendo || acciones.soloLectura;
  const valida = isAddress(dir);

  return (
    <Bloque titulo={t("El robot")}>
      <p className="detalle">
        {t("Es lo ÚNICO que puede hacer: reajustar tu rango. No puede sacar dinero ni cambiar ajustes.")}
      </p>
      <div className="aj-stat">
        <span className="aj-stat-label">{t("Operador actual")}</span>
        <code className="aj-stat-valor dir">{corto(v.operador)}</code>
      </div>

      <div className="aj-botones-robot">
        <button className="btn" disabled={ocupado} onClick={() => pedir({
          titulo: v.pausado ? t("Reanudar el robot") : t("Pausar el robot"),
          descripcion: v.pausado
            ? <>{t("El robot vuelve a reajustar tu rango cuando el precio se salga.")}</>
            : <>{t("El robot deja de reajustar. Tu dinero no se mueve, pero si el precio se sale dejas de cobrar y nadie lo corrige.")}</>,
          boton: v.pausado ? t("Reanudar") : t("Pausar"),
          peligro: !v.pausado,
          ejecutar: () => acciones.pausar(!v.pausado),
        })}>
          {v.pausado ? t("Reanudar el robot") : t("Pausar el robot")}
        </button>
        <button className="btn peligro" disabled={ocupado}
          onClick={() => pedir({
            titulo: t("Detener el robot"),
            descripcion: <>{t("Quita el operador del vault. Nadie podrá reajustar tu rango hasta que vuelvas a poner uno.")}</>,
            detalles: [[t("Tu dinero"), t("no se mueve, sigue en la posición")],
                       [t("Reajustes"), t("ninguno, hasta que pongas otro operador")]],
            aviso: <>{t("Si el precio se sale del rango, dejas de cobrar y nadie lo corregirá.")}</>,
            boton: t("Detener el robot"),
            peligro: true,
            ejecutar: () => acciones.detenerRobot(),
          })}>
          {t("Detener el robot")}
        </button>
      </div>

      <label className="campo">
        <span>{t("Cambiar robot")} <em>{t("dirección")}</em></span>
        <input value={dir} onChange={(e) => setDir(e.target.value)} placeholder="0x…" disabled={ocupado} />
      </label>
      <button className="btn" disabled={ocupado || !valida}
        onClick={() => pedir({
          titulo: t("Cambiar el robot"),
          descripcion: <>{t("Solo esa dirección podrá reajustar tu rango. Seguirá sin poder mover tu dinero ni cambiar tus ajustes.")}</>,
          detalles: [[t("Ahora"), corto(v.operador)], [t("Después"), corto(dir)]],
          aviso: <>{t("Si pones una dirección que no controlas, tu vault se queda sin gestión automática.")}</>,
          peligro: true,
          boton: t("Cambiar robot"),
          ejecutar: () => acciones.cambiarOperador(dir as Address),
        })}>{t("Cambiar robot")}</button>
    </Bloque>
  );
}

function Actualizar({ v, acciones, pedir }: { v: EstadoVault; acciones: Acc; pedir: Pedir }) {
  const t = useT();
  const ocupado = !!acciones.haciendo || acciones.soloLectura;
  const { puntos, ocultas } = novedadesDe(v.logicaPublicada, v.colchonSoportado);
  const hayNovedades = puntos.length > 0;
  return (
    <Bloque titulo={t("Actualizar la lógica")}>
      <p className="detalle">{t("Hay una versión más reciente del código que gestiona tu posición.")}</p>
      {hayNovedades && (
        <div className="novedades">
          <span className="novedades-titulo">{t("Qué mejora")}</span>
          <ul>
            {puntos.map((p, i) => (
              <li key={i}><span className="tic" aria-hidden>✓</span>{t(p)}</li>
            ))}
          </ul>
          {ocultas && (
            <p className="novedades-nota">
              {t("Algunas mejoras nuevas solo están en vaults creados desde cero con esta versión; en este vault se adopta el algoritmo de reajuste mejorado.")}
            </p>
          )}
        </div>
      )}
      <p className="detalle">
        {t("Actualizar")} <strong>{t("no mueve tu dinero")}</strong>, {t("no cierra la posición ni cambia tus ajustes: solo cambia el código que ejecuta los reajustes de ahora en adelante. Solo tú puedes hacerlo.")}
      </p>
      <div className="aj-versiones">
        <div className="aj-version">
          <span>{t("Ahora")}</span>
          <code>{corto(v.rebalancer)}</code>
        </div>
        <span className="aj-flecha">→</span>
        <div className="aj-version">
          <span>{t("Disponible")}</span>
          <code>{corto(v.logicaPublicada)}</code>
        </div>
      </div>
      <button className="btn" disabled={ocupado} onClick={() => pedir({
        titulo: t("Actualizar la lógica"),
        descripcion: (
          <>
            {t("Tu vault pasa a ejecutar el código que publica su factory.")} <strong>{t("No mueve tu dinero")}</strong>,
            {t("no cierra la posición ni cambia tus ajustes.")}
            {hayNovedades && (
              <ul className="confirm-novedades">
                {puntos.map((p, i) => <li key={i}>{t(p)}</li>)}
              </ul>
            )}
          </>
        ),
        detalles: [[t("Ahora"), corto(v.rebalancer)], [t("Después"), corto(v.logicaPublicada)]],
        aviso: <>{t("Solo tú puedes hacerlo, y solo puede apuntar a lo que publica tu factory. Nadie más puede cambiar tu código.")}</>,
        boton: t("Actualizar"),
        ejecutar: () => acciones.actualizarLogica(),
      })}>
        {acciones.haciendo === "actualizarLogica" ? <Giro texto={t("Actualizando…")} /> : t("Actualizar a la última versión")}
      </button>
    </Bloque>
  );
}

/**
 * AVISOS POR TELEGRAM. Es el unico bloque de Ajustes que no firma una transaccion: firma un MENSAJE
 * —gratis, sin gas, sin tocar el vault— y eso es justo lo que hay que decir en pantalla, porque una
 * wallet pidiendo firma asusta igual valga dinero o no.
 *
 * El chat id no se pide. Se emite un enlace de un solo uso que abre el bot ya escrito: nadie sabe
 * cual es su chat id, y mandar a la gente a buscarlo pierde a la mitad por el camino.
 */
function AvisosTelegram({ v, red }: { v: EstadoVault; red: RedKeel }) {
  const t = useT();
  const avisos = useAvisos(v.direccion, red);
  const d = avisos.data;
  const ocupado = !!avisos.haciendo;

  // Sin bot configurado en el servidor no hay nada que ofrecer, y un formulario que no hace nada es
  // peor que no enseñar el bloque.
  if (avisos.isError || (d && d.bot === null)) {
    return (
      <Bloque titulo={t("Avisos por Telegram")}>
        <p className="detalle">
          {t("Los avisos no están disponibles ahora mismo.")} {avisos.isError
            ? t("No pude hablar con el servidor de avisos.")
            : t("El bot todavía no está configurado en el servidor.")}
        </p>
      </Bloque>
    );
  }

  return (
    <Bloque titulo={d?.conectado ? t("Avisos por Telegram · activos") : t("Avisos por Telegram")}>
      <p className="detalle">
        {t("Recibe notificaciones de")} <strong>{t("todas tus piscinas")}</strong> {t("en las tres redes: cuando se salen de rango, cuando el robot las recentra, cuando el gas o la cuota se quedan cortos.")}
      </p>
      <p className="detalle tenue">
        {t("Se conecta una sola vez — las piscinas que abras después entran solas. Se firma un")} <strong>{t("mensaje")}</strong> {t("(sin gas, sin transacción).")}
      </p>

      {avisos.error && (
        <div className="aviso error" onClick={avisos.limpiar}>
          <p className="detalle">{avisos.error}</p>
        </div>
      )}

      {avisos.isPending && <p className="detalle">{t("Consultando…")}</p>}

      {d && !d.conectado && (
        <>
          {avisos.enlace && (
            <div className="aviso">
              <p className="detalle">
                {t("Abre el enlace y pulsa")} <strong>Start</strong> {t("en Telegram. Esta pantalla se pone al día sola en cuanto lo hagas.")}
                <br />
                <a href={avisos.enlace} target="_blank" rel="noreferrer">{t("Abrir Telegram ↗")}</a>
              </p>
            </div>
          )}
          <button className="btn" disabled={ocupado} onClick={() => avisos.conectar()}>
            {avisos.haciendo === "conectar" ? <Giro texto={t("Firmando…")} /> : t("Conectar Telegram")}
          </button>
        </>
      )}

      {d?.conectado && (
        <>
          <div className="aj-stat">
            <span className="aj-stat-label">{t("Chat conectado")}</span>
            <code className="aj-stat-valor">{d.chatParcial}</code>
          </div>
          <div className="aj-stat">
            <span className="aj-stat-label">{t("Piscinas vigiladas")}</span>
            <span>
              <strong>{d.vaultsVigilados ?? 0}</strong>
              {d.vaultsVigilados === 0 && t(" — se añaden solas al abrir la primera")}
            </span>
          </div>
          <p className="detalle">{t("Qué quieres que te avise:")}</p>
          {TIPOS_AVISO.map((tipo) => (
            <label className="ck" key={tipo.clave}>
              <input
                type="checkbox"
                checked={d.avisos?.[tipo.clave] ?? true}
                disabled={ocupado}
                onChange={(e) =>
                  avisos.guardar({ ...d.avisos, [tipo.clave]: e.target.checked })
                }
              />
              <span>{t(tipo.etiqueta)} <em className="detalle">— {t(tipo.ayuda)}</em></span>
            </label>
          ))}
          <button className="btn fantasma" disabled={ocupado} onClick={() => avisos.desconectar()}>
            {avisos.haciendo === "baja" ? <Giro texto={t("Desconectando…")} /> : t("Desconectar Telegram")}
          </button>
        </>
      )}
    </Bloque>
  );
}

function Campo({ etiqueta, val, set, disabled }: {
  etiqueta: string; val: string; set: (s: string) => void; disabled?: boolean;
}) {
  return (
    <label className="campo">
      <span>{etiqueta}</span>
      <input type="number" value={val} onChange={(e) => set(e.target.value)} disabled={disabled} step="any" />
    </label>
  );
}
